/*
  https://openlayers.org/en/latest/examples/static-image.html
 */

import React, {useEffect} from "react";
import "ol/ol.css";
import "./MapView.css";
import Map from "ol/Map";
import View from "ol/View";
import ImageLayer from "ol/layer/Image.js";
import Projection from "ol/proj/Projection.js";
import Static from "ol/source/ImageStatic.js";
import {getCenter} from "ol/extent.js";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import OLStyle from "ol/style/Style";
import OLCircleStyle from "ol/style/Circle";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import OLStroke from "ol/style/Stroke";
import OLFill from "ol/style/Fill";
import {WalkMap} from "./types";
import {useFormikContext} from "formik";
import {MapBrowserEvent} from "ol";
import {useEditContext} from "./EditContext";
import PointEdit from "./PointEdit";
import {LineString} from "ol/geom";

const checkpointStyle = new OLStyle({
  image: new OLCircleStyle({
    radius: 15,
    stroke: new OLStroke({
      color: "#fff",
      width: 5,
    }),
    fill: new OLFill({
      color: "#33CC99",
    }),
  }),
});

const pointStyle = new OLStyle({
  image: new OLCircleStyle({
    radius: 7,
    stroke: new OLStroke({
      color: "#fff",
    }),
    fill: new OLFill({
      color: "#33CC99",
    }),
  }),
});

const pathStyle = new OLStyle({
  stroke: new OLStroke({
    color: "#33CC99",
    lineDash: [3, 10],
    width: 5,
  }),
});

const pointSource = new VectorSource();
const checkpointSource = new VectorSource();
const pathSource = new VectorSource();

const checkpointLayer = new VectorLayer({
  source: checkpointSource,
  style: checkpointStyle,
});

const pointLayer = new VectorLayer({
  source: pointSource,
  style: pointStyle,
});

const pathLayer = new VectorLayer({
  source: pathSource,
  style: pathStyle,
});

const map = new Map({});

const MapView = () => {
  const {values: walkMap, setFieldValue} = useFormikContext<WalkMap>();
  const {updatingCoordinate, setUpdatingCoordinate, setEditNode} =
    useEditContext();

  useEffect(() => {
    map.setTarget("map");
  }, []);

  useEffect(() => {
    if (!walkMap.image) {
      return;
    }

    // Map views always need a projection.  Here we just want to map image
    // coordinates directly to map coordinates, so we create a projection that uses
    // the image extent in pixels.
    const extent = [0, 0, walkMap.image.width, walkMap.image.height];
    const projection = new Projection({
      code: "walkmap-image",
      units: "pixels",
      extent: extent,
    });
    map.setLayers([
      new ImageLayer({
        source: new Static({
          url: walkMap.image.url,
          projection: projection,
          imageExtent: extent,
        }),
      }),
      pathLayer,
      checkpointLayer,
      pointLayer,
    ]);
    map.setView(
      new View({
        projection: projection,
        center: getCenter(extent),
        zoom: 2,
        maxZoom: 8,
      }),
    );
  }, [walkMap.image]);

  useEffect(() => {
    map.getListeners("click")?.forEach(listener => {
      map.removeEventListener("click", listener);
    });

    if (updatingCoordinate !== null) {
      map.addEventListener("click", evt => {
        const coordinate = (evt as MapBrowserEvent<any>).coordinate;
        const x = Math.round(coordinate[0]);
        const y = Math.round(coordinate[1]);
        const updatedPoints = [...walkMap.points];

        if (updatingCoordinate === -1) {
          updatedPoints.push({x, y});
        } else {
          updatedPoints[updatingCoordinate].x = x;
          updatedPoints[updatingCoordinate].y = y;
        }

        setFieldValue("points", updatedPoints);
        setUpdatingCoordinate(null);
        setEditNode(
          <PointEdit
            index={
              updatingCoordinate === -1
                ? updatedPoints.length - 1
                : updatingCoordinate
            }
          />,
        );
      });
    }
  }, [
    updatingCoordinate,
    walkMap.points,
    setFieldValue,
    setUpdatingCoordinate,
    setEditNode,
  ]);

  useEffect(() => {
    const pointFeatures: Feature[] = [];
    const checkpointFeatures: Feature[] = [];
    const pathFeatures: Feature[] = [];

    for (let i = 0; i < walkMap.points.length; i++) {
      const feature = new Feature(
        new Point([walkMap.points[i].x, walkMap.points[i].y]),
      );
      if (walkMap.points[i].content) {
        checkpointFeatures.push(feature);
      } else {
        pointFeatures.push(feature);
      }

      if (i > 0) {
        pathFeatures.push(
          new Feature(
            new LineString([
              [walkMap.points[i - 1].x, walkMap.points[i - 1].y],
              [walkMap.points[i].x, walkMap.points[i].y],
            ]),
          ),
        );
      }
    }

    pointSource.clear();
    checkpointSource.clear();
    pathSource.clear();

    pointSource.addFeatures(pointFeatures);
    checkpointSource.addFeatures(checkpointFeatures);
    pathSource.addFeatures(pathFeatures);
  }, [walkMap.points]);

  return (
    <div
      id="map"
      className={`MapView ${updatingCoordinate ? "updating" : ""}`}
    />
  );
};

export default MapView;
